import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
const allReviews = [{
  name: 'Michael T.',
  role: 'Homeowner',
  location: 'Tacoma, WA',
  content: 'On time and easy to work with. I needed 5 yards of crushed rock for a driveway extension. They gave me a quote instantly and delivered it the next morning.'
}, {
  name: 'Sarah J.',
  role: 'Landscaping Contractor',
  location: 'Puyallup, WA',
  content: 'Best pricing in the Tacoma area. We use 253Gravel for all our South King County projects. They actually show up when they say they will.'
}, {
  name: 'David W.',
  role: 'Homeowner',
  location: 'Federal Way, WA',
  content: 'Delivered exactly where I needed it. The material calculator was super helpful—I ordered exactly what I needed for my patio base. Great quality road base.'
}, {
  name: 'Karen L.',
  role: 'Homeowner',
  location: 'Lakewood, WA',
  content: 'We redid our entire backyard with their beauty bark and drain rock. Driver was super careful around the landscaping. Will definitely order again next spring.'
}, {
  name: 'James R.',
  role: 'General Contractor',
  location: 'Spanaway, WA',
  content: "Reliable and consistent quality. We've used them on three different job sites this year. The online ordering makes it easy to schedule around our project timelines."
}, {
  name: 'Lisa M.',
  role: 'Homeowner',
  location: 'University Place, WA',
  content: 'I had no idea how much gravel I needed, but their calculator nailed it. Got the 5/8 minus for my walkway and it looks amazing. Super fast delivery too.'
}, {
  name: 'Tom H.',
  role: 'Property Manager',
  location: 'Tacoma, WA',
  content: 'We manage several rental properties and use 253Gravel for all our gravel and bark needs. Pricing is transparent and the delivery guys are always professional.'
}, {
  name: 'Amanda P.',
  role: 'Homeowner',
  location: 'Bonney Lake, WA',
  content: 'Ordered 10 yards of beauty bark for our front and back yards. It was delivered within 24 hours and the quality was way better than the big box stores.'
}, {
  name: 'Chris D.',
  role: 'Landscaping Contractor',
  location: 'Graham, WA',
  content: "The bulk discount on bark saved us a ton on a large commercial project. Communication was great from quote to delivery. These guys know what they're doing."
}, {
  name: 'Rachel K.',
  role: 'Homeowner',
  location: 'Sumner, WA',
  content: "First time ordering bulk material and they made it so easy. The driver called ahead and placed it exactly where I asked. Couldn't be happier with the 1.25 clear."
}, {
  name: 'Brian S.',
  role: 'Excavation Contractor',
  location: 'Eatonville, WA',
  content: "We go through a lot of quarry rock and drain rock. 253Gravel has been the most consistent supplier we've worked with in Pierce County. Fair prices, no games."
}, {
  name: 'Nicole F.',
  role: 'Homeowner',
  location: 'Gig Harbor, WA',
  content: 'Needed gravel for a French drain project. They helped me pick the right size and delivered it the same week. The whole process from quote to delivery was seamless.'
}];
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
const REVIEWS_PER_PAGE = 3;
export function Reviews() {
  const shuffledReviews = useMemo(() => shuffleArray(allReviews), []);
  const totalPages = Math.ceil(shuffledReviews.length / REVIEWS_PER_PAGE);
  const [page, setPage] = useState(0);
  const currentReviews = shuffledReviews.slice(page * REVIEWS_PER_PAGE, (page + 1) * REVIEWS_PER_PAGE);
  return <section className="relative py-28 bg-[#f3f0ec] dark:bg-[#292827] transition-colors duration-300 border-y border-stone-300 dark:border-[#574f4a]" id="reviews" data-id="element-727">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-728">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6" data-id="element-729">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-730">
            <p className="text-sm font-bold text-brand-dim dark:text-brand-muted uppercase tracking-widest mb-3" data-id="element-731">
              Testimonials
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-stone-200 tracking-tight" data-id="element-732">
              Trusted by the 253
            </h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.1
        }} className="flex items-center gap-3" data-id="element-733">
            <div className="flex -space-x-0.5" data-id="element-734">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-5 h-5 fill-[#d4a017] text-[#d4a017]" data-id="element-735" />)}
            </div>
            <span className="text-stone-500 dark:text-stone-400 text-sm font-medium" data-id="element-736">
              4.9 from 50+ reviews
            </span>
          </motion.div>
        </div>

        {/* Reviews Grid */}
        <AnimatePresence mode="wait" data-id="element-737">
          <motion.div key={page} initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-738">
            {currentReviews.map((review, index) => <motion.div key={review.name} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: index * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className="bg-[#f2f1ed] dark:bg-[#3a3939] p-8 rounded-2xl border border-stone-200 dark:border-[#4a4848] flex flex-col h-full shadow-sm dark:shadow-none" data-id="element-739">
                <div className="flex gap-1 mb-6" data-id="element-740">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-[#d4a017] text-[#d4a017]" data-id="element-741" />)}
                </div>
                <p className="text-stone-700 dark:text-stone-300 leading-relaxed mb-8 flex-grow text-[15px]" data-id="element-742">
                  "{review.content}"
                </p>
                <div className="flex items-center gap-4 mt-auto pt-6 border-t border-stone-500 dark:border-[#323335]" data-id="element-743">
                  <div className="w-10 h-10 rounded-full bg-stone-400 dark:bg-[#262829] flex items-center justify-center text-stone-700 dark:text-stone-200 font-bold font-heading" data-id="element-744">
                    {review.name.charAt(0)}
                  </div>
                  <div data-id="element-745">
                    <p className="text-stone-900 dark:text-stone-200 font-bold text-sm" data-id="element-746">
                      {review.name}
                    </p>
                    <p className="text-stone-500 dark:text-stone-500 text-xs" data-id="element-747">
                      {review.role} • {review.location}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </AnimatePresence>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-4 mt-10" data-id="element-748">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="p-2 rounded-full border border-stone-600 dark:border-[#3a3b3e] text-stone-800 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-200 hover:border-stone-700 dark:hover:border-[#4b4c4f] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-749">
            <ChevronLeft className="w-5 h-5" data-id="element-750" />
          </button>

          <div className="flex gap-2" data-id="element-751">
            {Array.from({
            length: totalPages
          }).map((_, i) => <button key={i} onClick={() => setPage(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === page ? 'bg-brand-orange scale-110' : 'bg-stone-600 dark:bg-[#323335] hover:bg-stone-700 dark:hover:bg-[#404143]'}`} data-id="element-752" />)}
          </div>

          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1} className="p-2 rounded-full border border-stone-600 dark:border-[#3a3b3e] text-stone-800 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-200 hover:border-stone-700 dark:hover:border-[#4b4c4f] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-753">
            <ChevronRight className="w-5 h-5" data-id="element-754" />
          </button>
        </div>
      </div>
    </section>;
}